import React, { type ReactNode } from 'react';
import { useTheme } from '@emotion/react';
import styled from 'types/theme.types';
import Divider from 'components/common/divider';
import { useNavbarCollapse } from 'components/layout/navbar/navbar-collapse-context';

export type MenuSectionTitle = {
  key: string;
  title: ReactNode;
  isSectionTitle: true;
};

type NavbarMenuSectionTitleProps = {
  item: MenuSectionTitle;
};

/**
 * Figma's section label has no rule and antd's Divider always draws one, so there is nothing to restyle
 * it into. `data-e2e` lands on both branches. APO-484 deletes the Divider branch.
 */
const NavbarMenuSectionTitle: React.FunctionComponent<NavbarMenuSectionTitleProps> = ({ item }) => {
  const { isCollapsed } = useNavbarCollapse();
  const { isNewDesign } = useTheme();

  if (isNewDesign) {
    return (
      <SectionLabelContainer data-e2e={item.key}>
        {!isCollapsed && <SectionLabel>{item.title}</SectionLabel>}
      </SectionLabelContainer>
    );
  }

  return (
    <SectionTitleContainer data-e2e={item.key}>
      <Divider margin="0" title={isCollapsed ? undefined : item.title} />
    </SectionTitleContainer>
  );
};

/**
 * New-design only. Figma nests each group in its own column at 16/8/4; the rail renders one flat list,
 * so the label carries the difference on top of PanelSection's 4px gap.
 */
const SectionLabelContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 var(--padding-md);
  margin-top: var(--padding-md);
  margin-bottom: var(--spacing-xxxs);
`;

const SectionLabel = styled.span`
  font-size: var(--font-size-labels-label-md);
  line-height: var(--line-height-labels-label-md);
  color: var(--text-body-on-dark);
`;

const SectionTitleContainer = styled.div`
  padding: 4px 15px 0;

  && .ant-divider {
    border-block-start-color: ${({ theme }) => theme.tertiaryGray};
  }

  && .ant-divider-inner-text {
    font-size: 11px;
    font-weight: bold;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.lightGray};
  }
`;

export default NavbarMenuSectionTitle;
